import React from 'react';

type DisableScrollingOptions = {
  keepOverflowY?: boolean;
  preserveScrollPosition?: boolean;
};

const defaultDisableScrollingOptions: DisableScrollingOptions = {
  keepOverflowY: false,
  preserveScrollPosition: false,
};

export const domHooks = {
  useClickOutside(
    ref: React.RefObject<HTMLDivElement>,
    callback: (event: Event) => void,
    capture?: boolean
  ) {
    const clickListener = React.useCallback((event: Event) => {
      if (ref.current && !ref.current.contains(event.target as HTMLDivElement)) {
        callback(event);
      }
    }, [ref, callback]);

    React.useEffect(() => {
      window.addEventListener('click', clickListener, {capture: capture || false});
      return () => {
        window.removeEventListener('click', clickListener, {capture: capture || false});
      };
    }, [clickListener, capture]);
    
    return clickListener;
  },
  useDisableScrolling(
    expanded: boolean,
    { keepOverflowY, preserveScrollPosition } = defaultDisableScrollingOptions,
  ) {
    const scrollTopRef = React.useRef<null | number>(null);
    React.useEffect(() => {
      document.body.style.overflowY = keepOverflowY ? 'visible' : 'scroll';
      if (expanded) {
        if (preserveScrollPosition) {
          const { scrollTop } = document.documentElement;
          scrollTopRef.current = scrollTop;
          document.body.style.top = `-${scrollTop}px`;
        }
        document.body.style.position = 'fixed';
        document.documentElement.style.minHeight = '100vh';
      } else {
        document.body.style.position = 'static';
        document.body.style.top = '0';
        if (preserveScrollPosition && scrollTopRef.current) {
          document.documentElement.scrollTop = scrollTopRef.current;
        }
      }

      return () => {
        document.body.style.overflowY = 'visible';
        document.body.style.position = 'static';
        document.documentElement.style.removeProperty('min-height');
      };
    }, [expanded, keepOverflowY, preserveScrollPosition]);
  },
};